import type { TenantDetail } from '../lib/api';

/** Live count of mailboxes that have completed OAuth into the
 * tenant's sequencer (Instantly / Smartlead / …). Separate from the
 * provisioning bar because mailboxes keep flipping `connected` as
 * background OAuth completes — operators want a real-time number
 * here even after the pipeline reads "Setup complete." */
export default function SequencerConnectionsCard({ tenant }: { tenant: TenantDetail }) {
  const mc: any = tenant.mailbox_counts || {};
  const connected = Number(mc.connected ?? mc.oauth_done ?? 0);
  const errors = Number(mc.errors ?? mc.oauth_error ?? 0);
  const total = Number(mc.total ?? 0);

  if (total <= 0) return null;

  const pct = Math.max(0, Math.min(100, Math.round((connected / total) * 100)));
  const isLive = tenant.status === 'running' || tenant.status === 'rerun_queued';
  // Mirror the server's 90% green threshold so the bar visually matches
  // the tenant.status the server hands back.
  const fillColor = connected / total >= 0.9 ? 'bg-ok'
    : errors > 0 && connected + errors >= total ? 'bg-warn'
    : 'bg-accent';

  const label = (tenant.sequencer || 'instantly')
    .replace(/^\w/, c => c.toUpperCase());

  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-dim mb-1">{label} connections</div>
          <div className="text-sm font-medium text-ink">
            {connected === total
              ? `All ${total} mailboxes connected`
              : connected / total >= 0.9
                ? `${connected} of ${total} mailboxes connected — ready to send`
                : `${connected} of ${total} mailboxes connected`}
          </div>
        </div>
        <div className="text-2xl tabular-nums font-semibold text-ink">
          {connected}<span className="text-dim text-base font-normal">/{total}</span>
        </div>
      </div>
      <div className="h-2 rounded-full overflow-hidden bg-white/[0.04]">
        <div
          className={`h-full ${fillColor} transition-all duration-500 ${isLive ? 'animate-pulse' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {errors > 0 && (
        <div className="mt-2 text-[11px] text-warn tabular-nums">
          {errors} mailbox{errors === 1 ? '' : 'es'} need attention
        </div>
      )}
    </div>
  );
}
